/**
 * Tiny date helpers — dependency-free, deterministic, easy to test.
 * All comparisons happen in the local browser TZ; the Worker uses the same
 * functions for queue calculation but sources `now` from the request time.
 */

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function daysBetween(a: Date, b: Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/** Parse a date string flexibly — Airtable returns ISO strings or yyyy-mm-dd. */
export function parseDate(value: string | undefined | null): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d;
}

export function isToday(value: string | undefined, now: Date = new Date()): boolean {
  const d = parseDate(value);
  if (!d) return false;
  return startOfDay(d).getTime() === startOfDay(now).getTime();
}

export function isOverdue(value: string | undefined, now: Date = new Date()): boolean {
  const d = parseDate(value);
  if (!d) return false;
  return endOfDay(d).getTime() < startOfDay(now).getTime();
}

export function isThisWeek(value: string | undefined, now: Date = new Date()): boolean {
  const d = parseDate(value);
  if (!d) return false;
  // Week starts Monday — sensible for an ops tool.
  const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const monday = startOfDay(now);
  monday.setDate(monday.getDate() - dayOfWeek);
  const sunday = endOfDay(monday);
  sunday.setDate(sunday.getDate() + 6);
  return d.getTime() >= monday.getTime() && d.getTime() <= sunday.getTime();
}

export function isOlderThanDays(
  value: string | undefined,
  days: number,
  now: Date = new Date(),
): boolean {
  const d = parseDate(value);
  if (!d) return false;
  return daysBetween(d, now) > days;
}

/** Format minutes as `1h 23m` / `45m` / `2h`. */
export function formatDuration(minutes: number | undefined): string {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Format a date as "Aug 12" or "Aug 12, 2025" depending on year. */
export function formatShortDate(value: string | undefined, now: Date = new Date()): string {
  const d = parseDate(value);
  if (!d) return '—';
  const sameYear = d.getFullYear() === now.getFullYear();
  const month = d.toLocaleString('en-US', { month: 'short' });
  return sameYear ? `${month} ${d.getDate()}` : `${month} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Format a relative date like "in 3 days", "today", "5 days ago". */
export function formatRelativeDate(value: string | undefined, now: Date = new Date()): string {
  const d = parseDate(value);
  if (!d) return '—';
  const diff = daysBetween(now, d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  if (diff > 0) return `in ${diff} days`;
  return `${Math.abs(diff)} days ago`;
}
